import { getPanelDefaultRoute } from "@src/router/routes";
import { CURRENCY, STATUS_BADGE } from "../constants/panel/common";
// ** UseJWT import to get config
import { confirmAlert } from "./toast";
import sample from "lodash/sample";
import size from "lodash/size";
import times from "lodash/times";
// import  secureLocalStorage  from  "react-secure-storage";

/**
 * Store Management
 * @param key
 * @param store
 */
export const getItemFromStore = (key, store = localStorage) => {
  try {
    const item = store.getItem(key);
    return JSON.parse(item);
  } catch (error) {
    console.error(`Error parsing item for key "${key}":`, error);
    return store.getItem(key); // fallback: return as-is
  }
};
export const setItemToStore = (key, payload, store = localStorage) => store.setItem(key, JSON.stringify(payload));
export const removeItemFromStore = (key, store = localStorage) => {
  return store.removeItem(key);
};

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note type field is just for showing purpose it's not used by anything in frontend
 ** We are checking type just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userType Type of user
 **/
export const getHomeRouteForLoggedInUser = (userType) => {
  if (userType) {
    return getPanelDefaultRoute();
  }
  return "/login";
};

export const destroyUserSession = () => {
  sessionStorage.clear();
  localStorage.clear();
};

/**
 * It returns the ability object from the store if it exists, otherwise it returns the
 * superAdminAbility object from the store
 * @returns The ability of the user.
 */
export const getAbility = () => {
  return getItemFromStore("ability");
};

/**
 * It returns the value of the token stored in the browser's local storage, if it exists
 * @returns The token from the local storage.
 */
export const handleToken = () => {
  return getItemFromStore("token");
};

/*
 * The numberCommaFormatter function takes a number as input and returns a string representation of the
 * number with commas separating the thousands.
 * @param number - The `number` parameter is the number that you want to format with commas.
 * @returns the input number formatted with commas.
 */
export const numberCommaFormatter = (number) => {
  let x = number;
  if (typeof number === "number") {
    x = x.toLocaleString();
  }
  return x || 0;
};

/**
 *  Statistics Formatter
 * @param num
 */
export const statFormatter = (num) => {
  num = num ? num.toString().replace(/[^0-9.]/g, "") : 0;
  if (num < 10000) {
    const typeCastNum = Number(num);
    if (Number.isInteger(typeCastNum)) {
      return num || 0;
    } else {
      return typeCastNum.toFixed(2);
    }
  }
  const si = [
    { v: 1e3, s: "K" },
    { v: 1e6, s: "M" },
    { v: 1e9, s: "B" },
    { v: 1e12, s: "T" },
    { v: 1e15, s: "P" },
    { v: 1e18, s: "E" }
  ];
  let index;
  for (index = size(si) - 1; index > 0; index--) {
    if (num >= si[index].v) {
      break;
    }
  }
  return (
    (num / si[index].v).toFixed(2).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, "$1") +
    si[index].s
  );
};

/**
 * Function to extract initials from content
 * @param nameString
 */
export const getInitials = (nameString = "") => {
  let initials = "";
  nameString = nameString.replace(/\s+/g, " ");
  const fullName = nameString.trim().split(" ");
  if (size(fullName) === 1) {
    initials = fullName[0][0];
  } else {
    initials = fullName[0][0] + fullName[1][0];
  }
  return initials?.toUpperCase();
};

/**
 * It tries to fetch a file from Google's ad server, and if it fails, it returns true, otherwise it
 * returns false
 * @returns A promise.
 */
export const checkForAdBlocker = () => {
  function isAdBlockActive (callback) {
    // Create a test element
    const testElement = document.createElement("div");
    testElement.innerHTML = "&nbsp;";
    testElement.className = "ad";

    // Append the test element to the document
    document.body.appendChild(testElement);

    // Wait for a short period
    setTimeout(function () {
      // Check if the test element has been affected by an ad blocker
      const isBlocked = testElement.offsetHeight === 0;

      // Remove the test element from the document
      testElement.remove();

      // Execute the callback with the result
      callback(isBlocked);
    }, 100);
  }

  // Usage
  isAdBlockActive(function (result) {
    if (result) {
      confirmAlert({
        title            : "Caution! Disable Ad Block",
        text             : "Please disable ad blocker based plugins, else the panel will not work as expected.",
        icon             : "warning",
        showCancelButton : false,
        confirmButtonText: "Refresh",
        preConfirm       : () => {
          window.location.reload();
        }
      });
    }
  });
};

export const removeEmptyFields = (obj = {}) => {
  // If the input is not an object, return it
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  // Recursively remove empty fields from nested objects
  for (const [key, value] of Object.entries(obj)) {
    obj[key] = removeEmptyFields(value);
    // Skip Date objects
    if (obj[key] instanceof Date) {
      continue;
    }
    // Trim string values
    if (typeof obj[key] === "string") {
      obj[key] = obj[key].trim();
    }

    if (obj[key] === undefined || obj[key] === null || obj[key] === "") {
      delete obj[key];
    }
    if (typeof obj[key] === "object" && size(Object.keys(obj[key])) === 0) {
      delete obj[key];
    }
  }
  return obj;
};

/**
 * to get badge for status as per status constants
 * @param {*} data
 * @returns color and title
 */
export function getStatusBadge (data) {
  let color = "primary";
  let title = "";
  if (data) {
    color = `${STATUS_BADGE[data]?.color || "primary"}`;
    title = STATUS_BADGE[data]?.title || data;
  }
  return { color, title };
}

/**
* React Select Theme Colors
@param theme
*/
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#7367f01a", // for option hover bg-color
    primary  : "oklch(0.32 0.03 255)", // for selected option bg-color
    neutral10: "#ffe600", // for tags bg-color
    neutral20: "#ededed", // for input border-color
    neutral30: "#ededed" // for input hover border-color
  }
});

/**
 * It toggles the value of the menuCollapsed key in the localStorage and then dispatches an event to
 * the window object
 */
export function handleMenuCollapsed (value) {
  setItemToStore(
    "menuCollapsed",
    value === "false" ? true : !getItemFromStore("menuCollapsed")
  );
  window.dispatchEvent(new Event("storage"));
}

export const generateRandomString = (length = 10) => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  return times(length, () => sample(characters)).join("");
};

/**
 * It toggles the value of the menuCollapsed key in the localStorage and then dispatches an event to
 * the window object
 */
export function handleSkin (value) {
  setItemToStore("skin", value);
  window.dispatchEvent(new Event("storage"));
}

export const formatCurrency = (value, currency = CURRENCY) => {
    const symbol = currency === "USD" ? "$" : "₹";
    const locale = currency === "USD" ? "en-US" : "en-IN";
    return `${symbol}${Number(value || 0).toLocaleString(locale)}`;
};

const BYTES_IN_MB = 1024 * 1024;

/**
 * Human-readable file size, e.g. "712 KB" / "1.4 MB".
 * @param bytes
 */
export const formatFileSize = (bytes) => {
    if (!bytes || bytes < 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < BYTES_IN_MB) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / BYTES_IN_MB).toFixed(1)} MB`;
};
